import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  UserIcon,
  XMarkIcon,
  CheckIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline'
import { getUsers, type User } from '@/api/usersApi'
import { getRoles } from '@/api/rolesApi'

interface SearchableUserSelectProps {
  value?: string
  onChange: (userId: string | undefined, user: User | undefined) => void
  placeholder?: string
  className?: string
  error?: string
  label?: string
  required?: boolean
  roleFilter?: string // Filter users by specific role
  excludeIds?: string[] // Exclude specific user IDs
  institutionId?: string // For super admin - filter by institution
}

export const SearchableUserSelect: React.FC<SearchableUserSelectProps> = ({
  value,
  onChange,
  placeholder = "Search and select user...",
  className = "",
  error,
  label,
  required = false,
  roleFilter,
  excludeIds = [],
  institutionId
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMorePages, setHasMorePages] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | undefined>()
  const [resolvedRoleId, setResolvedRoleId] = useState<string | undefined>()

  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const PAGE_SIZE = 20

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  // Find selected user by value
  useEffect(() => {
    if (value) {
      const user = users.find(u => u.id === value)
      if (user) {
        setSelectedUser(user)
      } else {
        // Fetch user details if not in current list
        fetchUserDetails(value)
      }
    } else {
      setSelectedUser(undefined)
    }
  }, [value, users])

  // Resolve role filter to role ID
  useEffect(() => {
    if (roleFilter && !resolvedRoleId) {
      const resolveRoleId = async () => {
        try {
          const rolesResponse = await getRoles()
          const role = rolesResponse.data.find(r => 
            r.name.toLowerCase().includes(roleFilter.toLowerCase())
          )
          if (role) {
            setResolvedRoleId(role.id)
            console.log(`Resolved role "${roleFilter}" to ID:`, role.id)
          }
        } catch (error) {
          console.error('Failed to resolve role filter:', error)
        }
      }
      resolveRoleId()
    }
  }, [roleFilter, resolvedRoleId])

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isOpen) {
        fetchUsers(1, true) // Reset to page 1 on new search
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchQuery, isOpen, resolvedRoleId])

  const fetchUsers = async (page: number = 1, resetList: boolean = false) => {
    setLoading(true)
    try {
      console.log('Fetching users:', { page, search: searchQuery, roleFilter, institutionId })
      
      const params: any = {
        page,
        limit: PAGE_SIZE,
        status: 'active'
      }
      
      if (searchQuery.trim()) {
        params.search = searchQuery.trim()
      }
      
      if (resolvedRoleId) {
        params.roleId = resolvedRoleId
      }
      
      if (institutionId) {
        params.institutionId = institutionId
      }
      
      const response = await getUsers(params)
      console.log('Users response:', response)
      
      // Filter out excluded users
      const filteredUsers = response.users.filter(user => !excludeIds.includes(user.id))
      
      if (resetList) {
        setUsers(filteredUsers)
      } else {
        setUsers(prev => [...prev, ...filteredUsers])
      }
      
      setCurrentPage(page)
      setHasMorePages(response.pagination?.hasNextPage || false)
      
    } catch (error: any) {
      console.error('Failed to fetch users:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchUserDetails = async (userId: string) => {
    try {
      // This would be used to fetch details of a pre-selected user
      // For now, we'll just leave it as the ID
      console.log('Should fetch user details for:', userId)
    } catch (error) {
      console.error('Failed to fetch user details:', error)
    }
  }

  const loadMoreUsers = () => {
    if (hasMorePages && !loading) {
      fetchUsers(currentPage + 1, false)
    }
  }

  const handleUserSelect = (user: User) => {
    setSelectedUser(user)
    onChange(user.id, user)
    setIsOpen(false)
    setSearchQuery('')
    setCurrentPage(1)
  }

  const handleClear = () => {
    setSelectedUser(undefined)
    onChange(undefined, undefined)
    setSearchQuery('')
    setCurrentPage(1)
  }

  const handleOpen = () => {
    setIsOpen(true)
    if (users.length === 0) {
      fetchUsers(1, true)
    }
  }

  const getRoleColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'student':
        return 'bg-blue-100 text-blue-800'
      case 'teacher':
        return 'bg-green-100 text-green-800'
      case 'parent':
        return 'bg-purple-100 text-purple-800'
      case 'driver':
        return 'bg-orange-100 text-orange-800'
      case 'school admin':
        return 'bg-indigo-100 text-indigo-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {/* Selected User Display / Trigger */}
      <div
        onClick={handleOpen}
        className={`
          form-input cursor-pointer flex items-center justify-between
          ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
          ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''}
        `}
      >
        {selectedUser ? (
          <div className="flex items-center space-x-3 flex-1">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
              {selectedUser.profileImage ? (
                <img
                  src={selectedUser.profileImage}
                  alt={selectedUser.fullName}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <UserIcon className="h-4 w-4 text-white" />
              )}
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">{selectedUser.fullName}</div>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500">{selectedUser.email}</span>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getRoleColor(selectedUser.role)}`}>
                  {selectedUser.role}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleClear()
              }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <XMarkIcon className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between w-full">
            <span className="text-gray-500">{placeholder}</span>
            <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 mt-1 w-full min-w-96 bg-white border border-gray-300 rounded-lg shadow-xl max-h-96 overflow-hidden"
          >
            {/* Search Input */}
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Users List */}
            <div className="max-h-72 overflow-y-auto">
              {loading && users.length === 0 ? (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-3">Searching users...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="p-6 text-center">
                  <UserCircleIcon className="h-10 w-10 text-gray-400 mx-auto" />
                  <p className="text-sm text-gray-500 mt-3">
                    {searchQuery ? 'No users found matching your search' : 'No users available'}
                  </p>
                </div>
              ) : (
                <>
                  {users.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleUserSelect(user)}
                      className="w-full px-5 py-4 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none flex items-center space-x-4"
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                        {user.profileImage ? (
                          <img
                            src={user.profileImage}
                            alt={user.fullName}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <UserIcon className="h-5 w-5 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">{user.fullName}</span>
                          {selectedUser?.id === user.id && (
                            <CheckIcon className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-sm text-gray-500">{user.email}</span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}>
                            {user.role}
                          </span>
                          {user.emailVerified && (
                            <CheckIcon className="h-3 w-3 text-green-600" />
                          )}
                        </div>
                      </div>
                    </button>
                  ))}

                  {/* Load More Button */}
                  {hasMorePages && (
                    <div className="border-t border-gray-200 p-3">
                      <button
                        type="button"
                        onClick={loadMoreUsers}
                        disabled={loading}
                        className="w-full py-2 px-4 text-sm text-blue-600 hover:bg-blue-50 rounded-md disabled:opacity-50"
                      >
                        {loading ? (
                          <div className="flex items-center justify-center space-x-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            <span>Loading more...</span>
                          </div>
                        ) : (
                          `Load more users (Page ${currentPage + 1})`
                        )}
                      </button>
                    </div>
                  )}

                  {/* Results Info */}
                  {users.length > 0 && (
                    <div className="border-t border-gray-200 px-4 py-2 bg-gray-50">
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>
                          Showing {users.length} users
                          {searchQuery && ` for "${searchQuery}"`}
                        </span>
                        <span>
                          {hasMorePages ? `Page ${currentPage} (more available)` : `Page ${currentPage}`}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Hook for easier user selection with state management
export const useUserSelection = (initialUserId?: string) => {
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(initialUserId)
  const [selectedUser, setSelectedUser] = useState<User | undefined>()

  const handleUserChange = (userId: string | undefined, user: User | undefined) => {
    setSelectedUserId(userId)
    setSelectedUser(user)
  }

  const clearSelection = () => {
    setSelectedUserId(undefined)
    setSelectedUser(undefined)
  }

  return {
    selectedUserId,
    selectedUser,
    handleUserChange,
    clearSelection
  }
}

// Simplified version for forms with react-hook-form
interface SearchableUserSelectFieldProps extends SearchableUserSelectProps {
  name: string
  control?: any
  setValue: (name: string, value: any) => void
}

export const SearchableUserSelectField: React.FC<SearchableUserSelectFieldProps> = ({
  name,
  setValue,
  ...props
}) => {
  const handleChange = (userId: string | undefined, user: User | undefined) => {
    setValue(name, userId)
    if (props.onChange) {
      props.onChange(userId, user)
    }
  }

  return <SearchableUserSelect {...props} onChange={handleChange} />
}

export default SearchableUserSelect